import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { zoomMeetingTime, zoomMeetingTitle, meetingTranscriptTitle, zoomAnalysisAction, fmtDuration, transcriptSourceInfo, parseTranscriptLines, analysisStatusInfo, hasTranscriptText, hasLinkedZoomMeeting } from "@/lib/zoomMeeting";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  SectionCard, EmptyState, DataTable, Pill, StatusBadge, ProgressBar,
  ActionButton, TimelineItem, WorkflowPipeline, SearchBar, FilterBar,
} from "@/components/shared";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import EngagementLetters from "./EngagementLetters";
import CommProvenanceTimeline from "@/components/CommProvenanceTimeline";
import TaxProfilePanel from "./TaxProfilePanel";
import { DependencyDashboard } from "./DependencyDashboard";
import { ServiceRecommendations } from "./ServiceRecommendations";
import ConversationWorkspace from "./ConversationWorkspace";
import { useAuth } from "@/context/AuthContext";
import StaffContactEditor from "@/components/StaffContactEditor";
import WorkspaceDocumentCenter from "./WorkspaceDocumentCenter";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCallMenu } from "@/components/PhoneCallMenu";
import AllisonReconciliationPanel from "@/components/workspace/AllisonReconciliationPanel";
import UploadTranscriptDialog from "@/components/workspace/UploadTranscriptDialog";
import { CockpitRail } from "@/components/OperationalCockpit";
import DRLPanel from "@/components/DRLPanel";
import FollowUpOpportunityCard from "./FollowUpOpportunityCard";

const LEAD_SOURCE_OPTIONS = [
  { value: "web_search", label: "Web Search" },
  { value: "friend", label: "Friend" },
  { value: "social_media", label: "Social Media" },
  { value: "referral", label: "Referral" },
];
const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString() : "—");

const STATUS_TONES = {
  uploaded: "success", approved: "success", awaiting_review: "warning", missing: "danger",
  rejected: "danger", requested: "info", active: "success", draft: "neutral",
  scheduled: "info", completed: "success", sent: "info", signed: "success",
};

/* ---------------------------------------------------------------- helpers */
const FIRM_LABELS = {
  "expat-tax-firm": "Expat Tax Firm", "mobile-tax-firm": "Mobile Tax Firm",
  "filetaxesfast": "FileTaxesFast", "quickbooks-cleaners": "QuickBooks Cleaners",
};

function ContactLine({ icon, label, value, href, actionIcon }) {
  const Icon = Icons[icon] || Icons.Circle;
  const ActionIcon = actionIcon ? (Icons[actionIcon] || null) : null;
  const has = Boolean(value);
  const inner = !has
    ? <span className="text-slate-400 italic">Not set</span>
    : href
      ? <a href={href} className="text-slate-800 hover:text-brand-gold-dark break-words transition-colors">{value}</a>
      : <span className="text-slate-800 break-words">{value}</span>;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`contact-${label.toLowerCase()}`}>
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${has ? "bg-slate-100" : "bg-slate-50"}`}>
        <Icon className={`h-[18px] w-[18px] ${has ? "text-slate-500" : "text-slate-300"}`} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-sm font-medium leading-snug">{inner}</div>
      </div>
      {ActionIcon && (
        <button
          type="button"
          disabled={!has}
          onClick={() => { if (has && href) window.location.href = href; }}
          title={has ? `Open ${label}` : `${label} not configured`}
          data-testid={`contact-${label.toLowerCase()}-action`}
          className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${has ? "bg-brand-gold/10 text-brand-gold-dark hover:bg-brand-gold/20" : "bg-slate-50 text-slate-300 cursor-not-allowed"}`}
        >
          <ActionIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function AddrBlock({ text, needsReview }) {
  const has = Boolean(text);
  return (
    <div className="flex items-start gap-3 py-2" data-testid="contact-mailing-address">
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${has ? "bg-slate-100" : "bg-slate-50"}`}>
        <Icons.MapPin className={`h-[18px] w-[18px] ${has ? "text-slate-500" : "text-slate-300"}`} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">Mailing Address</div>
        {has
          ? <div className="text-sm font-medium leading-snug text-slate-800 whitespace-pre-line break-words">{text}</div>
          : <div className="text-sm font-medium leading-snug"><span className="text-slate-400 italic">Not set</span></div>}
        {needsReview && has && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600" data-testid="contact-address-review-cue">
            <Icons.Info className="h-3 w-3" aria-hidden="true" /> Legacy address — review recommended
          </div>
        )}
      </div>
    </div>
  );
}

function PhoneRow({ label, value, type, testid }) {
  const has = Boolean(value);
  const opts = has ? [{ type, label, value }] : [];
  const Icon = type === "home" ? Icons.PhoneCall : Icons.Smartphone;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={testid}>
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${has ? "bg-slate-100" : "bg-slate-50"}`}>
        <Icon className={`h-[18px] w-[18px] ${has ? "text-slate-500" : "text-slate-300"}`} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{label} Phone</div>
        <div className="text-sm font-medium leading-snug">
          {has ? <span className="text-slate-800 break-words">{value}</span> : <span className="text-slate-400 italic">Not set</span>}
        </div>
      </div>
      <PhoneCallMenu options={opts} testid={`${testid}-call`} />
    </div>
  );
}

function ContactInfoCard({ c, onPortalMessage, clientId, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const src = c.referral_source || (c.lead_source ? c.lead_source.replace(/_/g, " ") : null);
  const pTone = c.priority_level === "high" || c.priority_level === "urgent" ? "danger" : c.priority_level === "low" ? "neutral" : "info";
  const addr = c.formatted_mailing_address || "";
  const needsReview = c.address_migration_status === "needs_review";

  const focusEdit = () => setTimeout(() => { document.querySelector('[data-testid="contact-edit-btn"]')?.focus(); }, 0);
  const done = async () => { setEditing(false); await onSaved?.(); focusEdit(); };
  const cancel = () => { setEditing(false); focusEdit(); };

  return (
    <SectionCard title="Contact Info" icon="Contact" data-testid="ws-contact-info"
      action={canEdit && !editing ? (
        <ActionButton variant="ghost" icon="Pencil" onClick={() => setEditing(true)} data-testid="contact-edit-btn">Edit</ActionButton>
      ) : null}>
      {editing ? (
        <StaffContactEditor contact={c} clientId={clientId} onSaved={done} onCancel={cancel}
          extraFields={[
            { key: "lead_source", label: "Lead Source", options: LEAD_SOURCE_OPTIONS, placeholder: "Select source" },
            { key: "priority_level", label: "Priority", options: PRIORITY_OPTIONS, default: "normal" },
          ]} />
      ) : (
        <>
          <div className="-mt-1">
            <ContactLine icon="Mail" label="Email" value={c.email} href={c.email ? `mailto:${c.email}` : null} actionIcon="Send" />
            <PhoneRow label="Mobile" type="mobile" value={c.mobile_phone} testid="contact-mobile-phone" />
            <PhoneRow label="Home" type="home" value={c.home_phone} testid="contact-home-phone" />
            <AddrBlock text={addr} needsReview={needsReview} />
            <ContactLine icon="MessageCircle" label="WhatsApp" value={c.whatsapp} actionIcon="MessageCircle" />
            <ContactLine icon="MessageSquare" label="LINE" value={c.line_id} actionIcon="MessageSquare" />
            <div className="flex items-center gap-3 py-2" data-testid="contact-portal-message">
              <span className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icons.ShieldCheck className="h-[18px] w-[18px] text-slate-500" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Portal Message</div>
                <div className="text-sm font-medium leading-snug text-slate-800">Secure portal</div>
              </div>
              <button type="button" onClick={onPortalMessage} title="Send portal message" aria-label="Send portal message" data-testid="contact-portal-message-action"
                className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 bg-brand-gold/10 text-brand-gold-dark hover:bg-brand-gold/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                <Icons.Send className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="border-t border-slate-100 mt-2 pt-2 grid grid-cols-2 gap-x-4">
            <Field label="Source" value={src ? <span className="capitalize">{src}</span> : "—"} />
            <div className="py-2 border-b border-slate-50">
              <div className="text-xs text-slate-400">Priority</div>
              <div className="mt-1"><Pill tone={pTone} className="capitalize">{c.priority_level || "normal"}</Pill></div>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

function Field({ label, value }) {
  return (
    <div className="py-2 border-b border-slate-50 last:border-0">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 font-medium mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
function Flag({ label, on }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {on ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Icons.MinusCircle className="h-4 w-4 text-slate-300" />}
      <span className={on ? "text-slate-800" : "text-slate-400"}>{label}</span>
    </div>
  );
}
function MiniCard({ label, value, sub, tone = "slate", icon }) {
  const Icon = icon ? (Icons[icon] || Icons.Circle) : null;
  const tones = { slate: "text-slate-800", rose: "text-rose-600", amber: "text-amber-600", emerald: "text-emerald-600", blue: "text-blue-600" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className={cn("text-2xl font-heading font-semibold mt-1", tones[tone])}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------- future-module shell tab */
function ShellTab({ icon, title, description, build, kpis = [], columns = [], actions = [] }) {
  return (
    <div className="space-y-5" data-testid={`shell-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2">
            {Icons[icon] && (() => { const I = Icons[icon]; return <I className="h-5 w-5 text-slate-400" />; })()}{title}
          </h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          {actions.map((a) => <ActionButton key={a} icon="Plus" variant="secondary" onClick={() => toast.info(`${title} actions arrive with ${build}`)}>{a}</ActionButton>)}
        </div>
      </div>
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((k) => <MiniCard key={k.label} {...k} />)}
        </div>
      )}
      <SectionCard>
        <FilterBar className="mb-4">
          <SearchBar value="" onChange={() => {}} placeholder={`Search ${title.toLowerCase()}…`} className="w-full sm:w-72" />
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"><Icons.SlidersHorizontal className="h-4 w-4" />Filters</button>
        </FilterBar>
        <DataTable columns={columns.map((c) => ({ key: c, label: c }))} rows={[]}
          empty={<EmptyState icon={icon} title={`No ${title.toLowerCase()} yet`}
            description={`This is a production-ready shell. ${build} will connect data and workflows here.`} />} />
      </SectionCard>
    </div>
  );
}

/* ── Case Manager Selector ─────────────────────────────────────────────────
   Shared engagement-scoped CM assignment control used by both the Engagements
   tab and the LiveOps deep-link entry point.
   Props:
     engId            — engagement UUID
     currentCmId      — current assigned_case_manager_id (or null)
     currentCmName    — display name for current CM
     onSaved(payload) — called after successful PATCH
     onClose()        — dismiss the dialog
   ──────────────────────────────────────────────────────────────────────── */
function CaseManagerSelector({ engId, currentCmId, currentCmName, onSaved, onClose }) {
  const [candidates, setCandidates] = useState(null);
  const [teamKey, setTeamKey]       = useState(null);
  const [selected, setSelected]     = useState(currentCmId || "__unassigned__");
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    api.get(`/engagements/${engId}/eligible-case-managers`)
      .then((r) => {
        setCandidates(r.data.items || []);
        setTeamKey(r.data.team_key || null);
      })
      .catch(() => setCandidates([]));
  }, [engId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/engagements/${engId}/case-manager`, {
        case_manager_id: (selected === "__unassigned__" ? null : selected) || null,
      });
      toast.success(r.data?.message || "Case Manager updated.");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign Case Manager.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="case-manager-selector-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Icons.UserCheck className="h-4 w-4 text-blue-600" />
            Assign Case Manager
          </DialogTitle>
          {teamKey && (
            <p className="text-xs text-slate-500 mt-0.5">
              Eligible staff: <span className="font-medium">{teamKey.replace(/_/g, " ")}</span> team
            </p>
          )}
        </DialogHeader>

        {candidates === null ? (
          <div className="py-6 flex justify-center">
            <Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="case-manager-select-trigger">
                <SelectValue placeholder="— Unassigned —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`cm-option-${c.id}`}>
                    {c.name}
                    {c.role && <span className="text-xs text-slate-400 ml-1">· {c.role.replace(/_/g, " ")}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {candidates.length === 0 && (
              <p className="text-xs text-amber-600">
                No eligible staff found for this engagement's team. Contact an administrator.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >Cancel</button>
          <button
            data-testid="case-manager-save-btn"
            disabled={saving || candidates === null}
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Check className="h-3.5 w-3.5" />}
            Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------- real engagements tab */
const ENG_STATUS_TONE = {
  generated: "bg-slate-100 text-slate-600",
  draft:     "bg-slate-100 text-slate-600",
  sent:      "bg-blue-100 text-blue-700",
  viewed:    "bg-indigo-100 text-indigo-700",
  signed:    "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-600",
  superseded:"bg-amber-100 text-amber-700",
};

function EngagementsTab({ clientId, intel = {}, onViewLetter, autoOpenEngId, autoOpenAction }) {
  const [rows, setRows]               = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [options, setOptions]         = useState(null);
  const [preparing, setPreparing]     = useState(null);
  // CS Activation CTA
  const [previews, setPreviews]       = useState({});   // { [engId]: activation-preview payload }
  const [alertOptions, setAlertOptions] = useState(null); // accepted estimates needing engagement
  const [activating, setActivating]   = useState(null); // engId currently being activated
  const [confirmEng, setConfirmEng]   = useState(null); // { eng, preview } — confirm modal data
  // Case Manager selector
  const [cmSelectorEngId, setCmSelectorEngId] = useState(null); // which engagement's CM selector is open
  // Production gate waiver
  const [gateEngId, setGateEngId]     = useState(null);  // which engagement's gate panel is open
  const [gateData, setGateData]       = useState(null);  // { activation_id, ready, requirements, blockers }
  const [gateLoading, setGateLoading] = useState(false);
  const [waivingReq, setWaivingReq]   = useState(null);  // { key, label } — requirement being waived
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverSaving, setWaiverSaving] = useState(false);
  const { user } = useAuth();
  // Gate C: use effective module membership — covers individual Finance grants (Sherry) as well as
  // role-based Finance access (administrator / partner / billing). Server is the authoritative boundary.
  const canViewFinance = user?.modules?.includes("finance");
  const canAssignCm = (user?.permission_keys || []).some((k) => ["*", "client_success.assign", "operations.manage"].includes(k));
  // Document waiver — production.waive_document or client_success.approve (or wildcard)
  const canWaiveDocument = (user?.permission_keys || []).some((k) =>
    ["*", "production.waive_document", "client_success.approve"].includes(k));

  const loadPreviews = useCallback((engs) => {
    const signed = (engs || []).filter((e) => e.engagement_status === "signed");
    if (!signed.length) return;
    Promise.allSettled(
      signed.map((e) =>
        api.get(`/client-success/engagements/${e.id}/activation-preview`)
          .then((res) => ({ id: e.id, data: res.data }))
      )
    ).then((results) => {
      const map = {};
      results.forEach((r) => { if (r.status === "fulfilled") map[r.value.id] = r.value.data; });
      setPreviews(map);
    });
  }, []);

  const openGatePanel = useCallback((eng) => {
    setGateEngId(eng.id);
    setGateData(null);
    setWaivingReq(null);
    setWaiverReason('');
    setGateLoading(true);
    api.get(`/client-success/activations/by-engagement/${eng.id}`)
      .then((r) => setGateData(r.data))
      .catch(() => setGateData({ error: true }))
      .finally(() => setGateLoading(false));
  }, []);

  const submitWaiver = useCallback(async (activationId) => {
    if (!waiverReason.trim() || !waivingReq) return;
    setWaiverSaving(true);
    try {
      await api.post(`/client-success/activations/${activationId}/waivers`, {
        requirement: waivingReq.key, reason: waiverReason.trim(),
      });
      // Refresh gate data
      const eng = (rows || []).find((e) => e.id === gateEngId);
      if (eng) {
        const r = await api.get(`/client-success/activations/by-engagement/${eng.id}`);
        setGateData(r.data);
      }
      setWaivingReq(null);
      setWaiverReason('');
    } catch { /* backend error surfaced via toast */ }
    setWaiverSaving(false);
  }, [waiverReason, waivingReq, rows, gateEngId]);

  const loadAlertOptions = useCallback(() => {
    api.get(`/sales/estimates/engagement-options/${clientId}`)
      .then((r) => setAlertOptions((Array.isArray(r.data) ? r.data : []).filter((o) => !o.existing_engagement)))
      .catch(() => setAlertOptions([]));
  }, [clientId]);

  const load = useCallback(() => {
    api.get(`/engagements?client_id=${clientId}&limit=50`)
      .then((r) => {
        const engs = Array.isArray(r.data) ? r.data : [];
        setRows(engs);
        loadPreviews(engs);
      })
      .catch(() => setRows([]));
  }, [clientId, loadPreviews]);

  useEffect(() => { load(); loadAlertOptions(); }, [load, loadAlertOptions]);

  // Deep-link: auto-open Case Manager selector when navigated from LiveOps
  useEffect(() => {
    if (autoOpenEngId && autoOpenAction === "assign_case_manager" && rows) {
      setCmSelectorEngId(autoOpenEngId);
    }
  }, [autoOpenEngId, autoOpenAction, rows]);

  const handleCmSaved = (payload) => {
    setCmSelectorEngId(null);
    // Refresh engagement rows so the CM name updates immediately
    load();
  };

  const openNewEngagement = async () => {
    setShowModal(true);
    if (!options) {
      try {
        const r = await api.get(`/sales/estimates/engagement-options/${clientId}`);
        setOptions(Array.isArray(r.data) ? r.data : []);
      } catch {
        setOptions([]);
      }
    }
  };

  const prepareEngagement = async (estimateId) => {
    setPreparing(estimateId);
    try {
      const r = await api.post(`/sales/estimates/${estimateId}/prepare-engagement`);
      toast.success(r.data?.message || "Engagement prepared successfully.");
      setShowModal(false);
      load();
      loadAlertOptions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to prepare engagement.");
    } finally {
      setPreparing(null);
    }
  };

  const activateEngagement = async (engId) => {
    setActivating(engId);
    try {
      const r = await api.post(`/client-success/engagements/${engId}/activate`, { force: false });
      toast.success(r.data?.message || "Client Success activated.");
      setConfirmEng(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed.");
    } finally {
      setActivating(null);
    }
  };

  const pending = (rows || []).filter((e) => !["cancelled","superseded"].includes(e.engagement_status));
  const complete = (rows || []).filter((e) => e.completed_at);

  return (
    <div className="space-y-5" data-testid="engagements-tab">
      {/* Accepted Estimate Alert — fires when accepted estimates lack an engagement letter */}
      {alertOptions && alertOptions.length > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="accepted-estimate-alert">
          <Icons.AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="flex-1 min-w-0 text-sm font-medium text-amber-800">
            {alertOptions.length === 1 ? "1 accepted estimate" : `${alertOptions.length} accepted estimates`}{" "}
            {alertOptions.length === 1 ? "is" : "are"} ready for engagement preparation
          </div>
          <button
            data-testid="alert-prepare-engagement-btn"
            onClick={openNewEngagement}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
          >
            <Icons.FilePlus className="h-3.5 w-3.5" /> Prepare Now
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniCard label="Open"      value={intel.open_engagements ?? pending.length}      tone="blue"    icon="Briefcase" />
        <MiniCard label="Signed"    value={(rows||[]).filter((e) => e.engagement_status === "signed").length}   tone="emerald" icon="CheckCircle" />
        <MiniCard label="Complete"  value={complete.length}  icon="Archive" />
        <MiniCard label="Cancelled" value={(rows||[]).filter((e) => e.engagement_status === "cancelled").length} icon="XCircle" />
      </div>

      {/* Engagement list */}
      <SectionCard
        title="Engagements"
        icon="Briefcase"
        description="All client engagement letters, teams, and production status."
        action={
          <ActionButton icon="Plus" variant="secondary" onClick={openNewEngagement} data-testid="new-engagement-btn">
            New Engagement
          </ActionButton>
        }
      >
        {rows === null ? (
          <div className="py-10 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon="Briefcase" title="No engagements yet"
            description="Create an engagement letter from an accepted estimate to begin onboarding." />
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((eng) => {
              const preview     = previews[eng.id];
              const isSigned    = eng.engagement_status === "signed";
              const isActivated = preview?.already_activated === true;
              const canActivate = isSigned && !isActivated;
              const isStr       = eng.is_str === true;
              // Derived STR production badge — no new lifecycle field, derived from canonical state
              // str_state comes from project_str_state() via crm.py related enrichment.
              // _ORGANIZER_WAITING_STATES: assignment assembled or sent to portal, client hasn't submitted.
              const _STR_ORGANIZER_WAITING = new Set(["assignment_pending","questionnaire_published"]);
              let strBadge = null;
              if (isStr) {
                if (!isActivated) {
                  strBadge = { label: "Ready to Activate",  cls: "bg-amber-100 text-amber-700",  icon: "Zap" };
                } else if (eng.str_state && _STR_ORGANIZER_WAITING.has(eng.str_state)) {
                  const wdays = eng.str_organizer_waiting_days;
                  const wLabel = wdays != null ? `Organizer Pending · ${wdays}d` : "Organizer Pending";
                  strBadge = { label: wLabel, cls: "bg-blue-100 text-blue-700", icon: "ClipboardList" };
                } else {
                  strBadge = { label: "STR Active",         cls: "bg-violet-100 text-violet-700", icon: "Rocket" };
                }
              }
              return (
                <div key={eng.id} className="py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 group" data-testid={`engagement-row-${eng.id}`}>
                  <div
                    className="min-w-0 flex-1 cursor-pointer"
                    title="View engagement letter"
                    onClick={() => onViewLetter?.(eng.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800 text-sm truncate">{eng.title || eng.engagement_type || "Engagement"}</span>
                      {eng.engagement_number && <span className="text-xs text-slate-400">{eng.engagement_number}</span>}
                      <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", ENG_STATUS_TONE[eng.engagement_status] || "bg-slate-100 text-slate-600")}>
                        {(eng.engagement_status || "").replace(/_/g," ")}
                      </span>
                      {eng.production_gate?.status === "cleared" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                          <Icons.ShieldCheck className="h-3 w-3" /> Production Ready
                        </span>
                      )}
                      {/* CS Activated badge — non-STR only; STR uses strBadge for richer context */}
                      {isActivated && !isStr && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" data-testid={`cs-activated-badge-${eng.id}`}>
                          <Icons.CheckCircle2 className="h-3 w-3" /> CS Activated
                        </span>
                      )}
                      {/* Derived STR production badge */}
                      {strBadge && (() => { const BadgeIcon = Icons[strBadge.icon]; return (
                        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium", strBadge.cls)} data-testid={`str-badge-${eng.id}`}>
                          {BadgeIcon && <BadgeIcon className="h-3 w-3" />}
                          {strBadge.label}
                        </span>
                      ); })()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                      {eng.sent_at && <span><span className="text-slate-400">Sent:</span> {fmtDate(eng.sent_at)}</span>}
                      {eng.signed_at && <span><span className="text-slate-400">Signed:</span> {fmtDate(eng.signed_at)}</span>}
                      {eng.assigned_team_id && <span><span className="text-slate-400">Team:</span> {eng.assigned_team_id}</span>}
                      {/* Case Manager — engagement-scoped canonical display */}
                      <span data-testid={`eng-case-manager-${eng.id}`}>
                        <span className="text-slate-400">Case Manager:</span>{" "}
                        {eng.assigned_case_manager_name
                          ? <span className="font-medium text-slate-700">{eng.assigned_case_manager_name}</span>
                          : <span className="italic text-slate-400">Unassigned</span>}
                        {canAssignCm && (
                          <button
                            data-testid={`assign-cm-btn-${eng.id}`}
                            onClick={(e) => { e.stopPropagation(); setCmSelectorEngId(eng.id); }}
                            className="ml-1.5 text-blue-500 hover:text-blue-700 transition-colors"
                            title="Assign Case Manager"
                          >
                            <Icons.Pencil className="inline h-3 w-3" />
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {eng.engagement_status === "sent" && (
                      <span className="text-xs text-amber-600 flex items-center gap-1"><Icons.Clock className="h-3.5 w-3.5" /> Awaiting signature</span>
                    )}
                    {/* Gate Requirements — shown for users with production.waive_document when gate is not cleared */}
                    {canWaiveDocument && eng.production_gate?.status && eng.production_gate.status !== "cleared" && (
                      <button
                        data-testid={`gate-requirements-btn-${eng.id}`}
                        onClick={() => openGatePanel(eng)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                        title="View gate requirements and waive blockers"
                      >
                        <Icons.ShieldAlert className="h-3.5 w-3.5" />
                        Gate Requirements
                      </button>
                    )}
                    {/* CS Activation CTA — shown only for signed, unactivated engagements */}
                    {canActivate && (
                      <button
                        data-testid={`activate-cs-btn-${eng.id}`}
                        disabled={activating === eng.id}
                        onClick={() => setConfirmEng({ eng, preview })}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {activating === eng.id
                          ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Icons.Rocket className="h-3.5 w-3.5" />}
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Case Manager Selector Modal */}
      {cmSelectorEngId && (() => {
        const eng = (rows || []).find((e) => e.id === cmSelectorEngId);
        return (
          <CaseManagerSelector
            engId={cmSelectorEngId}
            currentCmId={eng?.assigned_case_manager_id}
            currentCmName={eng?.assigned_case_manager_name}
            onSaved={handleCmSaved}
            onClose={() => setCmSelectorEngId(null)}
          />
        );
      })()}

      {/* Gate Requirements Panel — document waiver for production staff */}
      {gateEngId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="gate-requirements-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-800 text-lg flex items-center gap-2">
                <Icons.ShieldAlert className="h-5 w-5 text-amber-500" /> Gate Requirements
              </h3>
              <button
                data-testid="gate-requirements-close"
                onClick={() => { setGateEngId(null); setGateData(null); setWaivingReq(null); setWaiverReason(''); }}
                className="text-slate-400 hover:text-slate-600"
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>

            {gateLoading && (
              <div className="flex justify-center py-6">
                <Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            )}

            {gateData?.error && (
              <p className="text-sm text-red-600">Could not load gate requirements. Please try again.</p>
            )}

            {gateData && !gateData.error && !gateData.has_activation && (
              <p className="text-sm text-slate-500">No activation record found for this engagement.</p>
            )}

            {gateData?.has_activation && (
              <>
                <p className="text-sm text-slate-600">
                  {gateData.ready
                    ? "All production-readiness requirements are met."
                    : `${(gateData.blockers || []).length} blocker(s) remain before Production Ready.`}
                </p>

                <div className="space-y-2">
                  {(gateData.requirements || []).map((req) => (
                    <div
                      key={req.key}
                      data-testid={`gate-req-${req.key}`}
                      className={`flex items-start gap-3 p-3 rounded-xl border ${
                        req.met || req.waived
                          ? "bg-emerald-50 border-emerald-100"
                          : "bg-amber-50 border-amber-200"
                      }`}
                    >
                      <div className="pt-0.5">
                        {req.met || req.waived
                          ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          : <Icons.AlertCircle className="h-4 w-4 text-amber-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700">{req.label}</p>
                        {req.waived && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            Waived by {req.waived_by}{req.waived_reason ? ` — "${req.waived_reason}"` : ""}
                          </p>
                        )}
                        {/* Inline waiver reason form */}
                        {waivingReq?.key === req.key && (
                          <div className="mt-2 space-y-2">
                            <textarea
                              data-testid={`waiver-reason-input-${req.key}`}
                              value={waiverReason}
                              onChange={(e) => setWaiverReason(e.target.value)}
                              placeholder="Reason for waiving this requirement (required)…"
                              rows={2}
                              className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                            />
                            <div className="flex gap-2">
                              <button
                                data-testid={`waiver-confirm-btn-${req.key}`}
                                disabled={!waiverReason.trim() || waiverSaving}
                                onClick={() => submitWaiver(gateData.activation_id)}
                                className="inline-flex items-center gap-1 text-xs font-medium bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                              >
                                {waiverSaving ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Check className="h-3.5 w-3.5" />}
                                Confirm Waiver
                              </button>
                              <button
                                onClick={() => { setWaivingReq(null); setWaiverReason(''); }}
                                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Show Waive button for unmet, non-waived requirements */}
                      {!req.met && !req.waived && waivingReq?.key !== req.key && (
                        <button
                          data-testid={`waive-btn-${req.key}`}
                          onClick={() => { setWaivingReq(req); setWaiverReason(''); }}
                          className="shrink-0 text-xs font-medium text-amber-700 border border-amber-300 px-2.5 py-1 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                          Waive
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Activation Confirmation Modal */}
      {confirmEng && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="activation-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-800 text-lg flex items-center gap-2">
                <Icons.Rocket className="h-5 w-5 text-blue-600" /> Activate Client Success
              </h3>
              <button onClick={() => setConfirmEng(null)} className="text-slate-400 hover:text-slate-600">
                <Icons.X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600">
              Activating <strong>{confirmEng.eng.engagement_number || "this engagement"}</strong> will start the Client Success workflow.
            </p>
            {confirmEng.preview && (
              <div className="space-y-2 bg-slate-50 rounded-xl p-4 text-sm">
                <div className="flex items-center gap-2">
                  <Icons.CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span className="text-slate-700">Organizer: <strong>{confirmEng.preview.will_assign_organizer}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Icons.CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span className="text-slate-700">Portal: {confirmEng.preview.portal_action}</span>
                </div>
                {confirmEng.preview.lifecycle_change && confirmEng.preview.lifecycle_change !== "no change" && (
                  <div className="flex items-center gap-2">
                    <Icons.CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span className="text-slate-700">{confirmEng.preview.lifecycle_change}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmEng(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-activate-btn"
                disabled={activating === confirmEng.eng.id}
                onClick={() => activateEngagement(confirmEng.eng.id)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {activating === confirmEng.eng.id
                  ? <Icons.Loader2 className="h-4 w-4 animate-spin" />
                  : <Icons.Rocket className="h-4 w-4" />}
                Confirm Activation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Engagement Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-engagement-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-800 text-lg">Prepare Engagement Letter</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><Icons.X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-slate-500">
              Select an accepted estimate to prepare an engagement letter for e-signature.
            </p>
            {options === null ? (
              <div className="py-6 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
            ) : options.length === 0 ? (
              <EmptyState icon="FileText" title="No accepted estimates" description="Accept an estimate first to create an engagement letter." />
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {options.map((opt) => (
                  <div key={opt.estimate_id} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 text-sm truncate">{opt.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {opt.estimate_number && <span>{opt.estimate_number} · </span>}
                        {canViewFinance && opt.total > 0 && <span>{opt.currency} {opt.total.toLocaleString()} · </span>}
                        {opt.services?.map((s) => s.code || s.name).filter(Boolean).join(", ")}
                      </div>
                    </div>
                    {opt.existing_engagement ? (
                      <span className="text-xs text-slate-400 shrink-0">
                        {opt.existing_engagement.engagement_number} ({opt.existing_engagement.engagement_status})
                      </span>
                    ) : (
                      <button
                        data-testid={`prepare-engagement-btn-${opt.estimate_id}`}
                        disabled={!!preparing}
                        onClick={() => prepareEngagement(opt.estimate_id)}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {preparing === opt.estimate_id ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Plus className="h-3.5 w-3.5" />}
                        Prepare
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ===================================================================== */
// States that indicate an STR organizer has been submitted by the client.
const STR_SUBMITTED_STATES = new Set([
  "questionnaire_submitted", "analysis_queued", "analysis_running",
  "analysis_complete", "management_review", "review_complete", "cycle_complete",
  "awaiting_supplemental_client", "supplemental_notification_failed",
  "external_notification_pending", "external_delivered", "external_client_accessed",
]);

function OrganizerTab({ clientId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [strState, setStrState] = useState(undefined); // undefined = loading, null = no STR

  const load = useCallback(() => {
    // Fetch generic organizers and STR lifecycle state in parallel.
    Promise.all([
      api.get("/organizers").then((r) => (r.data.items || []).filter((o) => o.client_id === clientId)).catch(() => []),
      api.get(`/strategic-review/state/${clientId}`).then((r) => r.data).catch(() => null),
    ]).then(([orgs, str]) => {
      setRows(orgs);
      setStrState(str);
    }).catch(() => { setRows([]); setStrState(null); });
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const TONE = { complete: "bg-emerald-100 text-emerald-700", submitted: "bg-violet-100 text-violet-700", waiting_on_client: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", reopened: "bg-orange-100 text-orange-700", staff_interview: "bg-indigo-100 text-indigo-700", staff_data_entry: "bg-indigo-100 text-indigo-700" };

  // Show the STR submission entry when an STR has been submitted for this client.
  const strSubmitted = strState && strState.completion_id && STR_SUBMITTED_STATES.has(strState.state);
  const loading = rows === null || strState === undefined;

  return (
    <SectionCard title="Organizers" icon="ClipboardList" description="Dynamic tax organizers — completion tracking and review.">
      {loading ? <div className="py-8 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div> : (
        <>
          {/* P0 Viewer: STR submission entry — shown when the client has a submitted STR Organizer */}
          {strSubmitted && (
            <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
              data-testid="ws-str-submission-entry">
              <Icons.FileCheck2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-emerald-900">Strategic Tax Review Organizer</span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200"
                    data-testid="ws-str-submission-badge">Submitted</span>
                </div>
                {strState.completion_submitted_at && (
                  <div className="text-xs text-emerald-700 mt-0.5" data-testid="ws-str-submission-date">
                    {new Date(strState.completion_submitted_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                )}
                <div className="text-xs text-emerald-600 mt-0.5">
                  {strState.title || "Strategic Tax Review"} · Engagement {strState.engagement_id ? strState.engagement_id.slice(0, 8) : "—"}
                </div>
              </div>
              <button
                data-testid="ws-str-view-submission-btn"
                onClick={() => navigate(`/client-success/strategic-review/${clientId}?tab=submission`)}
                className="shrink-0 flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors">
                View Submitted Answers
                <Icons.ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Generic organizer rows */}
          {rows.length === 0 && !strSubmitted
            ? <EmptyState icon="ClipboardList" title="No organizer yet" description="An organizer is assigned automatically when an engagement is signed." />
            : rows.length > 0 && (
              <div className="space-y-2" data-testid="ws-organizers">
                {rows.map((o) => (
                  <button key={o.id} onClick={() => navigate(`/client-success/organizers/${o.id}`)} data-testid={`ws-org-${o.id}`}
                    className="w-full flex items-center justify-between rounded-lg border border-slate-200 hover:border-blue-400 px-4 py-3 text-left transition-colors">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{o.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{o.engagement_number || "—"} · {(o.completion_method || "").replace(/_/g, " ")}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2"><div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${o.progress}%` }} /></div><span className="text-xs text-slate-500">{o.progress}%</span></div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${TONE[o.status] || "bg-slate-100 text-slate-500"}`}>{(o.status || "").replace(/_/g, " ")}</span>
                      <Icons.ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </button>
                ))}
              </div>
            )
          }
        </>
      )}
    </SectionCard>
  );
}


function ProductionWsTab({ clientId }) {
  const navigate = useNavigate();
  const [recs, setRecs] = useState(null);
  const [strState, setStrState] = useState(null);

  useEffect(() => {
    api.get("/production/records")
      .then((r) => setRecs((r.data.items || []).filter((x) => x.client_id === clientId)))
      .catch(() => setRecs([]));
    // Reuse the canonical STR state projection — no new lifecycle system
    api.get(`/strategic-review/state/${clientId}`)
      .then((r) => setStrState(r.data))
      .catch(() => setStrState(null));
  }, [clientId]);

  const TONE = { production_ready: "neutral", assigned: "info", in_preparation: "info", internal_review: "info", corrections: "warning", client_review: "info", ready_to_file: "info", filed: "success", completed: "success" };
  const hasActiveStr = strState && strState.action_code === "open_str_workspace";

  return (
    <SectionCard title="Production" icon="Factory" description="Live production status — preparation through filing. No financial data.">
      {/* STR workspace entry — shown whenever client has an active Strategic Review cycle */}
      {hasActiveStr && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3">
          <Icons.FileSearch className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800">Strategic Tax Review Active</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              {strState.title || "Strategic Tax Review"} · {(strState.state || "").replace(/_/g, " ")}
            </p>
          </div>
          <button
            data-testid="prod-tab-str-workspace-btn"
            onClick={() => navigate(strState.workspace_target || `/client-success/strategic-review/${clientId}`)}
            className="shrink-0 flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors"
          >
            Open STR Workspace
            <Icons.ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {recs === null ? <div className="py-8 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div> :
       recs.length === 0 ? <EmptyState icon="Factory" title="Not in production yet" description="A production record is created automatically when the engagement is transferred from Client Success." /> : (
        <div className="space-y-2" data-testid="ws-production">
          {recs.map((r) => (
            <button key={r.id} onClick={() => navigate(`/production/${r.id}`)} data-testid={`ws-prod-${r.id}`} className="w-full flex items-center justify-between rounded-lg border border-slate-200 hover:border-blue-400 px-4 py-3 text-left transition-colors">
              <div><div className="text-sm font-medium text-slate-800">{r.engagement_number} · <span className="capitalize">{(r.service_type || "").replace(/_/g, " ")}</span></div>
                <div className="text-xs text-slate-400 mt-0.5">Preparer: {r.preparer} · Reviewer: {r.reviewer}{r.open_info_requests > 0 ? ` · ${r.open_info_requests} open request(s)` : ""}</div></div>
              <div className="flex items-center gap-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${r.waiting_status ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.waiting_label || r.stage_label}</span><Icons.ChevronRight className="h-4 w-4 text-slate-300" /></div>
            </button>
          ))}
        </div>
      )}
    </SectionCard>
  );
}


// ── TranscriptSlideOver ───────────────────────────────────────────────────────
// Presentation-only slide-over for viewing canonical transcript text.
// Fetches from GET /meeting-transcripts/{id}/full-transcript (read-only endpoint).
// No mutations — no analysis triggers, no downloads, no workflow actions.
function TranscriptSlideOver({ transcript, clientName, onClose }) {
  const [state, setState] = useState("loading"); // loading | loaded | unavailable | error
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!transcript?.id) return;
    setState("loading");
    setLines([]);
    api.get(`/meeting-transcripts/${transcript.id}/full-transcript`)
      .then(({ data }) => {
        const text = (data.transcript_text || "").trim();
        if (!text) { setState("unavailable"); return; }
        setLines(parseTranscriptLines(text));
        setState("loaded");
      })
      .catch(() => setState("error"));
  }, [transcript?.id]);

  const src      = transcriptSourceInfo(transcript || {});
  const title    = zoomMeetingTitle(transcript, clientName);
  const time     = zoomMeetingTime(transcript);
  const duration = fmtDuration((transcript?.meeting_context?.zoom || {}).duration_minutes);

  return (
    <Sheet open={!!transcript} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col p-0 gap-0"
        data-testid="transcript-slideover"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <SheetTitle className="text-base font-semibold text-slate-900 leading-snug truncate pr-8" data-testid="transcript-slideover-title">
            {title}
          </SheetTitle>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className={`inline-flex items-center text-xs font-semibold rounded-full px-2.5 py-0.5 ${
              src.tone === "info"    ? "bg-blue-50 text-blue-700 border border-blue-200" :
              src.tone === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              "bg-slate-100 text-slate-600 border border-slate-200"
            }`} data-testid="transcript-slideover-source">{src.label}</span>
            {time && <span className="text-xs text-slate-500" data-testid="transcript-slideover-date">{fmtDT(time)}</span>}
            {duration && <span className="text-xs text-slate-400">{duration}</span>}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="transcript-slideover-body">
          {state === "loading" && (
            <div className="flex items-center justify-center gap-2 py-20 text-slate-400" data-testid="transcript-slideover-loading">
              <Icons.Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading transcript…</span>
            </div>
          )}
          {state === "error" && (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center" data-testid="transcript-slideover-error">
              <Icons.AlertCircle className="h-8 w-8 text-rose-300" />
              <p className="text-sm font-medium text-slate-700">Unable to load transcript</p>
              <p className="text-xs text-slate-400">The transcript could not be retrieved. Try refreshing or check back later.</p>
            </div>
          )}
          {state === "unavailable" && (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center" data-testid="transcript-slideover-unavailable">
              <Icons.FileX2 className="h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">No transcript text available</p>
              <p className="text-xs text-slate-400">The meeting was imported but no transcript text was captured.</p>
            </div>
          )}
          {state === "loaded" && (
            <div className="space-y-1" data-testid="transcript-slideover-content">
              {lines.map((l, i) => (
                <div key={i} className="text-sm leading-relaxed py-0.5">
                  {l.speaker && <span className="font-semibold text-slate-700">{l.speaker}: </span>}
                  <span className="text-slate-600">{l.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function WorkspaceTabs({ bundle, related, documents, timeline, audit, clientId, reload, tab, setTab, engSection = "overview", setEngSection, openComposer, onOpenComposerHandled, cockpit, autoOpenEngId, autoOpenAction, drlData, drlLoading, runDrl, onDocumentAction }) {
  const navigate = useNavigate();
  const c = bundle.contact;
  const tax = bundle.tax_profile || {};
  const intel = bundle.intelligence || {};
  const qual = bundle.qualification || {};
  const wf = bundle.workflow;
  const [noteText, setNoteText] = useState("");
  const [taskText, setTaskText] = useState("");
  const { user } = useAuth();
  // Gate C: use effective module membership — covers individual Finance grants (Sherry) as well as
  // role-based Finance access (administrator / partner / billing). Server is the authoritative boundary.
  const canViewFinance = user?.modules?.includes("finance");
  const canEditCrm = !!user && (
    (user.permissions || []).includes("*") ||
    (user.permission_keys || []).includes("crm.edit") ||
    ((user.modules || []).includes("crm") && user.can_write)
  );
  // Tracks which engagement letter to open when switching to the letters sub-view
  const [selectedLetterEngId, setSelectedLetterEngId] = useState(null);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/crm/contacts/${clientId}/notes`, { body: noteText }); setNoteText(""); toast.success("Note added"); reload(); }
    catch (e) { toast.error("Could not add note"); }
  };
  const addTask = async () => {
    if (!taskText.trim()) return;
    try { await api.post(`/crm/contacts/${clientId}/tasks`, { title: taskText, priority: "normal" }); setTaskText(""); toast.success("Task created"); reload(); }
    catch (e) { toast.error("Could not create task"); }
  };
  const completeTask = async (tid) => { await api.post(`/crm/contacts/${clientId}/tasks/${tid}/complete`); reload(); };

  const docs = documents || { requests: [], uploaded_files: [], counts: {} };
  const tasks = related?.tasks || [];
  const notes = related?.notes || [];
  const meetings = related?.meetings || [];
  const zoomTranscripts = related?.zoom_transcripts || [];
  const manualTranscripts = related?.manual_transcripts || [];
  const [showUploadTranscript, setShowUploadTranscript] = useState(false);
  const [viewingTranscript, setViewingTranscript] = useState(null);

  // Deterministic name for title fallbacks — client or lead display name
  const contactName = bundle?.contact?.full_name || bundle?.lead?.full_name || "";

  // Canonical merged transcript library — deduplicated by ID, sorted newest-first.
  // Zoom transcripts appear once here (not a duplicate of the Zoom Meetings section
  // above — that section shows meeting-level context; this is the transcript library).
  // zoom_transcripts records are guaranteed provider=zoom by the DB query but the
  // /related projection omits the provider field — tag explicitly before merging.
  const allTranscripts = useMemo(() => {
    const seen = new Set();
    const zoomTagged = zoomTranscripts.map(tr => tr.provider ? tr : { ...tr, provider: "zoom" });
    return [...zoomTagged, ...manualTranscripts]
      .filter(tr => {
        if (!tr?.id || seen.has(tr.id)) return false;
        seen.add(tr.id);
        return true;
      })
      .sort((a, b) => {
        const ta = zoomMeetingTime(a) || "";
        const tb = zoomMeetingTime(b) || "";
        return tb.localeCompare(ta);
      });
  }, [zoomTranscripts, manualTranscripts]);
  const engagements = related?.engagements || [];
  const estimates = related?.estimates || [];
  const proposals = related?.proposals || [];
  const services = related?.recommended_services || [];
  // P0: Lifecycle-only invoice data from the already-sanitized CRM /related payload.
  // Non-financial users use this instead of calling /finance/invoices directly.
  const lifecycleInvoices = related?.invoices || [];

  return (
    <Tabs value={tab} onValueChange={setTab}>
      {/* OVERVIEW */}
      <TabsContent value="overview">
        {/* Allison — Next Action (DRL-1): compact, collapsible resolution-loop panel */}
        <div className="mb-5">
          <DRLPanel clientId={clientId} onNavigate={(tab) => setTab(tab)} drlData={drlData} drlLoading={drlLoading} runDrl={runDrl} onDocumentAction={onDocumentAction} primaryEngagementId={cockpit?.primary_engagement_id} />
        </div>
        {/* Follow-Up Opportunity — shows when a deferred/active/closed commercial follow-up exists */}
        {related?.follow_up && (
          <div className="mb-5">
            <FollowUpOpportunityCard followUp={related.follow_up} onReload={reload} />
          </div>
        )}
        {/* Row 1 — Identity: who is this lead */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ContactInfoCard c={c} onPortalMessage={() => setTab("communication")} clientId={clientId} canEdit={canEditCrm} onSaved={reload} />

          <SectionCard title="Tax Profile Summary" icon="FileUser"
            action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("tax")} data-testid="ov-view-tax-profile">View Full Tax Profile</ActionButton>}>
            <div className="grid grid-cols-2 gap-x-4">
              <Field label="Citizenship" value={c.citizenship} />
              <Field label="Country of Residence" value={c.country_of_residence} />
              <Field label="Tax Residency" value={tax.tax_residency} />
              <Field label="Filing Status" value={tax.filing_status} />
              <Field label="Dependents" value={tax.dependent_count ?? "—"} />
              <Field label="Business Owner" value={tax.business_owner ? "Yes" : "No"} />
            </div>
          </SectionCard>

          <SectionCard title="Current Engagements" icon="Briefcase">
            {engagements.length === 0 ? (
              <div className="flex flex-col items-center text-center py-4">
                <span className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center mb-2"><Icons.Briefcase className="h-5 w-5 text-slate-400" /></span>
                <div className="text-sm font-medium text-slate-700">No engagements</div>
                <p className="text-xs text-slate-400 mt-0.5 mb-3">Create an estimate to start a new engagement.</p>
                <ActionButton icon="FileSignature" variant="secondary" onClick={() => navigate(`/sales/estimates/new?client=${clientId}`)} data-testid="ov-create-estimate">Create Estimate</ActionButton>
              </div>
            ) : (
              engagements.map((e) => (
                <div key={e.id} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800">{e.template || "Engagement"}</span>
                    <StatusBadge status={e.status} map={STATUS_TONES} />
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{e.engagement_number}</div>
                </div>
              ))
            )}
          </SectionCard>
        </div>

        {/* Row 2 — Recent Activity (priority) + Documents at a glance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
          <div className="lg:col-span-2">
            <SectionCard title="Recent Activity" icon="Activity"
              action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("timeline")} data-testid="ov-view-timeline">View Timeline</ActionButton>}>
              {timeline.length === 0 ? <EmptyState icon="Activity" title="No activity yet" /> :
                <div className="space-y-4" data-testid="ov-recent-activity">
                  {timeline.slice(0, 10).map((e, i, arr) => (
                    <TimelineItem key={e.id} icon={e.icon} title={e.title} description={e.description} meta={fmtDT(e.created_at)} last={i === arr.length - 1} />
                  ))}
                </div>}
            </SectionCard>
          </div>

          <SectionCard title="Documents at a Glance" icon="FileText"
            action={<ActionButton variant="ghost" icon="ArrowRight" onClick={() => setTab("documents")} data-testid="ov-go-documents">Go to Documents</ActionButton>}>
            <div className="grid grid-cols-2 gap-3" data-testid="ov-docs-glance">
              {[["Requested", docs.counts.requested, "bg-slate-400"], ["Uploaded", docs.counts.uploaded, "bg-emerald-500"],
                ["Awaiting Review", docs.counts.awaiting_review, "bg-amber-500"], ["Missing", docs.counts.missing, "bg-rose-500"]].map(([l, v, dot]) => (
                <button key={l} type="button" onClick={() => setTab("documents")} data-testid={`ov-docs-${l.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex flex-col items-start rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">
                  <span className="text-2xl font-heading font-semibold text-slate-900">{v || 0}</span>
                  <span className="text-[11px] font-medium text-slate-500 mt-0.5 flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full shrink-0", dot)} aria-hidden="true" />{l}</span>
                </button>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Row 3 — Secondary reference cards: Routing Recommendation · Priority · Key Facts */}
        {cockpit && (
          <div className="mt-5" data-testid="ov-secondary-cards">
            <CockpitRail data={cockpit} onViewRouting={() => setTab("workflow")} />
          </div>
        )}

        {/* Row 4 — Conversation + Allison · Conversation Brief (bottom of Overview) */}
        <div className="mt-5" data-testid="ov-conversation-section">
          <ConversationWorkspace
            clientId={clientId}
            bundle={bundle}
            related={related}
            timeline={timeline}
            documents={docs}
            reload={reload}
            onNavigate={setTab}
          />
        </div>
      </TabsContent>

      {/* TAX PROFILE — master tax data record + qualification checklist (merged) */}
      <TabsContent value="tax">
        <TaxProfilePanel clientId={clientId} />
        <div className="mt-6">
          <SectionCard title="Qualification Checklist" icon="ClipboardCheck"
            action={<span className="text-sm font-semibold text-slate-600" data-testid="tax-qual-percent">{qual.percent || 0}%</span>}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">{qual.completed || 0} of {qual.total || 0} qualification fields completed</span>
            </div>
            <ProgressBar value={qual.percent || 0} tone={(qual.percent || 0) >= 75 ? "emerald" : (qual.percent || 0) >= 40 ? "amber" : "rose"} />
            {(qual.sections || []).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                {(qual.sections || []).map((sec) => (
                  <div key={sec.section} className="rounded-lg border border-slate-100 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{sec.section}</div>
                    <div className="space-y-2">
                      {sec.fields.map((f) => (
                        <div key={f.label} className="flex items-center justify-between text-sm">
                          <span className={f.present ? "text-slate-700" : "text-slate-400"}>{f.label}</span>
                          {f.present ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Pill tone="warning">Missing</Pill>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </TabsContent>

      {/* SERVICES */}
      <TabsContent value="services">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <SectionCard title="Active Engagements / Services" icon="Package">
            {engagements.length === 0 ? <EmptyState icon="Package" title="No active services" /> :
              engagements.map((e) => {
                // Display-name resolution — priority:
                // 1. is_str flag (dual-signal from str_assignments) → always "Strategic Tax Review"
                //    services_snapshot may be stale on engagements rebuilt before activation.
                // 2. services_snapshot[].name — commercial scope, authoritative
                // 3. title / template_name  — legacy letter title, treated as unverified fallback
                // 4. "Service name unavailable" — never a silent blank
                const isStr = e.is_str === true;
                const svcNames = (e.services_snapshot || []).map((s) => s?.name).filter(Boolean);
                const hasCommercialScope = svcNames.length > 0;
                const displayName = isStr
                  ? "Strategic Tax Review"
                  : (hasCommercialScope
                    ? svcNames.join(" / ")
                    : (e.title || e.template_name || null));
                const isLegacyFallback = !isStr && !hasCommercialScope && Boolean(displayName);
                return (
                  <div key={e.id} data-testid={`active-service-row-${e.id}`}
                    className="flex items-center justify-between border-b border-slate-50 py-2 last:border-0">
                    <div className="min-w-0 flex-1 mr-3">
                      {displayName
                        ? <span className={`text-sm ${isLegacyFallback ? "text-slate-500 italic" : "text-slate-800"}`}>
                            {displayName}
                          </span>
                        : <span className="text-sm text-slate-400 italic">
                            Service name unavailable
                            {e.engagement_number && <span className="ml-1 not-italic font-mono text-xs">({e.engagement_number})</span>}
                          </span>}
                    </div>
                    {/* Badge is derived from inclusion in the backend projection — superseded/cancelled are
                        filtered server-side, so every row here represents an active commercial service. */}
                    <StatusBadge status="active" map={STATUS_TONES} />
                  </div>
                );
              })}
          </SectionCard>
          <SectionCard title="Recommended Services & Cross-Sell" icon="Sparkles">
            {services.length === 0 ? <EmptyState icon="Sparkles" title="No recommendations" /> :
              <div className="space-y-2">
                {services.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                    <div><div className="text-sm font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400 capitalize">{s.category}</div></div>
                    <ActionButton variant="ghost" icon="Plus" onClick={() => toast.info("Add service arrives with the Sales module")}>Recommend</ActionButton>
                  </div>))}
              </div>}
          </SectionCard>
        </div>
      </TabsContent>

      {/* ESTIMATES (live) + future shells */}
      <TabsContent value="estimates">
        <div className="mb-5">
          <AllisonReconciliationPanel clientId={clientId} clientName={bundle?.client?.full_name || bundle?.contact?.full_name}
            onReconciled={() => reload?.()} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <MiniCard label="Total" value={estimates.length} icon="Calculator" tone="blue" />
          <MiniCard label="Sent" value={estimates.filter((e) => e.status === "sent").length} icon="Send" />
          <MiniCard label="Accepted" value={estimates.filter((e) => ["accepted", "payment_pending", "paid"].includes(e.status)).length} icon="CheckCircle" tone="emerald" />
          <MiniCard label="Converted" value={estimates.filter((e) => ["converted", "converted_to_invoice"].includes(e.status)).length} icon="FileSignature" />
        </div>
        <SectionCard title="Estimates" icon="Calculator" action={<ActionButton icon="Plus" variant="secondary" onClick={() => navigate(`/sales/estimates?create=1&client=${clientId}`)} data-testid="ws-new-estimate">Create Estimate</ActionButton>}>
          <DataTable
            columns={canViewFinance
              ? [{ key: "estimate_number", label: "Estimate #" }, { key: "total", label: "Amount", align: "right" }, { key: "status", label: "Status" }, { key: "created_at", label: "Created" }]
              : [{ key: "estimate_number", label: "Estimate #" }, { key: "status", label: "Status" }, { key: "created_at", label: "Created" }]}
            rows={estimates}
            onRowClick={(r) => navigate(`/sales/estimates/${r.id}`)}
            renderCell={(r, col) => col.key === "total" ? (r.total != null ? `$${r.total.toLocaleString()}` : "—")
              : col.key === "status" ? <StatusBadge status={r.status} map={STATUS_TONES} />
              : col.key === "created_at" ? fmtDate(r.created_at) : <span className="font-mono text-xs text-slate-500">{r.estimate_number}</span>}
            empty={<EmptyState icon="Calculator" title="No estimates yet" description="Create an estimate to begin the commercial workflow." />} />
        </SectionCard>
      </TabsContent>
      <TabsContent value="engagement_letters"><EngagementLetters clientId={clientId} reload={reload} /></TabsContent>

      {/* INVOICES */}
      <TabsContent value="invoices">
        <InvoicesWorkspaceTab clientId={clientId} canViewFinance={canViewFinance} lifecycleInvoices={lifecycleInvoices} />
      </TabsContent>

      <TabsContent value="provenance"><CommProvenanceTimeline clientId={clientId} /></TabsContent>
      <TabsContent value="engagements">
        {/* ── Internal sub-navigation: Engagements | Engagement Letters ── */}
        <div className="flex items-center gap-1 mb-5 p-1 bg-slate-100 rounded-lg w-fit" data-testid="eng-subnav">
          <button
            data-testid="eng-subnav-overview"
            onClick={() => setEngSection?.("overview")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              engSection !== "letters"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            <span className="flex items-center gap-1.5">
              <Icons.Briefcase className="h-3.5 w-3.5" />Engagements
            </span>
          </button>
          <button
            data-testid="eng-subnav-letters"
            onClick={() => setEngSection?.("letters")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              engSection === "letters"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-800"
            }`}>
            <span className="flex items-center gap-1.5">
              <Icons.FileSignature className="h-3.5 w-3.5" />Engagement Letters
            </span>
          </button>
        </div>

        {engSection === "letters" ? (
          <EngagementLetters clientId={clientId} reload={reload} defaultDetailId={selectedLetterEngId} />
        ) : (
          <>
            {engagements.length > 0 && (
              <div className="space-y-3 mb-5" data-testid="ws-engagement-dependencies">
                {engagements.map((e) => (
                  <div key={e.id}>
                    <div className="text-xs font-medium text-slate-500 mb-1.5">{e.engagement_number || e.template || "Engagement"}</div>
                    <div className="space-y-3">
                      <ServiceRecommendations engagementId={e.id} />
                      <DependencyDashboard engagementId={e.id} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <EngagementsTab
              clientId={clientId}
              intel={intel}
              autoOpenEngId={autoOpenEngId}
              autoOpenAction={autoOpenAction}
              onViewLetter={(engId) => {
                setSelectedLetterEngId(engId);
                setEngSection?.("letters");
              }}
            />
          </>
        )}
      </TabsContent>
      <TabsContent value="organizers"><OrganizerTab clientId={clientId} /></TabsContent>

      {/* DOCUMENTS */}
      <TabsContent value="documents">
        <WorkspaceDocumentCenter clientId={clientId} clientName={c.full_name} docs={docs} reload={reload} />
      </TabsContent>

      {/* COMMUNICATIONS — unified conversation workspace (Phase 2) */}
      <TabsContent value="communication">
        <ConversationWorkspace clientId={clientId} bundle={bundle} related={related} timeline={timeline}
          documents={docs} reload={reload} onNavigate={setTab}
          openComposer={openComposer} onOpenComposerHandled={onOpenComposerHandled} />
      </TabsContent>

      {/* MEETINGS */}
      <TabsContent value="meetings">
        {/* ── Summary counts ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <MiniCard label="Upcoming"     value={meetings.filter((m) => new Date(m.scheduled_start) >= new Date()).length} icon="CalendarClock" tone="blue" />
          <MiniCard label="Past"         value={meetings.filter((m) => new Date(m.scheduled_start) < new Date()).length}  icon="CalendarCheck" />
          <MiniCard label="Transcripts"  value={allTranscripts.length} icon="ScrollText" tone="blue" />
          <MiniCard label="Consultations" value={meetings.filter((m) => m.type === "consultation").length} icon="Users" />
        </div>

        {/* ── Scheduled meetings ──────────────────────────────────────────────── */}
        <SectionCard title="Scheduled Meetings" icon="Calendar"
          action={
            <div className="flex items-center gap-2">
              <ActionButton icon="Upload" variant="secondary" onClick={() => setShowUploadTranscript(true)} data-testid="meetings-upload-transcript-btn">
                Upload Transcript
              </ActionButton>
              <ActionButton icon="Plus" variant="secondary" onClick={() => toast.info("Scheduling arrives with the Communications module")}>Schedule</ActionButton>
            </div>
          }>
          <DataTable
            columns={[{ key: "title", label: "Meeting" }, { key: "type", label: "Type" }, { key: "scheduled_start", label: "When" }, { key: "status", label: "Status" }]}
            rows={meetings}
            renderCell={(r, col) => col.key === "scheduled_start" ? fmtDT(r.scheduled_start)
              : col.key === "type" ? <Pill tone="neutral" className="capitalize">{(r.type || "").replace(/_/g, " ")}</Pill>
              : col.key === "status" ? <StatusBadge status={r.status} map={STATUS_TONES} /> : r.title}
            empty={<EmptyState icon="Calendar" title="No scheduled meetings" />} />
        </SectionCard>

        {/* ── Zoom meeting history ─────────────────────────────────────────────── */}
        {/* Each row = one meeting occurrence: title → date/time · Zoom · duration → status → actions */}
        <SectionCard title="Zoom Meetings" icon="Video" className="mt-5">
          {zoomTranscripts.length === 0
            ? <EmptyState icon="Video" title="No Zoom meetings yet" />
            : zoomTranscripts.map((tr) => {
                const title    = zoomMeetingTitle(tr, contactName);
                const time     = zoomMeetingTime(tr);
                const duration = fmtDuration((tr?.meeting_context?.zoom || {}).duration_minutes);
                const action   = zoomAnalysisAction(tr, clientId);
                const anaStat  = analysisStatusInfo(tr?.analysis?.status);
                const showTranscriptReady = hasTranscriptText(tr) && !anaStat; // only when no analysis badge
                return (
                  <div key={tr.id} data-testid={`zoom-meeting-row-${tr.id}`}
                    className="flex items-start justify-between border-b border-slate-50 py-3 last:border-0 gap-3">
                    <div className="min-w-0 flex-1">
                      {/* Primary: meaningful title */}
                      <div className="text-sm font-medium text-slate-800 truncate">{title}</div>
                      {/* Secondary: date · source · duration */}
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {time && <span className="text-xs text-slate-500">{fmtDT(time)}</span>}
                        <span className="text-slate-300 text-xs">·</span>
                        <Pill tone="info" className="text-xs shrink-0">Zoom</Pill>
                        {duration && <><span className="text-slate-300 text-xs">·</span><span className="text-xs text-slate-400">{duration}</span></>}
                      </div>
                      {/* Status: Transcript Ready + Analysis state (not duplicated) */}
                      {(showTranscriptReady || anaStat) && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          {showTranscriptReady && (
                            <Pill tone="success" className="text-xs" data-testid={`zoom-transcript-ready-${tr.id}`}>Transcript Ready</Pill>
                          )}
                          {anaStat && (
                            <Pill tone={anaStat.tone} className="text-xs" data-testid={`zoom-analysis-status-${tr.id}`}>{anaStat.label}</Pill>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Actions: View Transcript + Review/View Analysis */}
                    <div className="flex items-center gap-2 shrink-0">
                      <ActionButton
                        data-testid={`zoom-meeting-view-transcript-${tr.id}`}
                        icon="ScrollText"
                        variant="ghost"
                        onClick={() => setViewingTranscript(tr)}
                        className="text-xs">
                        View Transcript
                      </ActionButton>
                      {action && (
                        <ActionButton
                          data-testid={`zoom-meeting-action-${tr.id}`}
                          icon="ArrowRight"
                          variant="secondary"
                          onClick={() => navigate(action.to)}
                          className="shrink-0 text-xs">
                          {action.label}
                        </ActionButton>
                      )}
                    </div>
                  </div>
                );
              })}
        </SectionCard>

        {/* ── Meeting Transcripts — canonical transcript library ───────────────── */}
        {/* Each row = one permanent transcript document: title → date/source/duration → status → linked → actions */}
        <SectionCard
          title={`Meeting Transcripts${allTranscripts.length ? ` (${allTranscripts.length})` : ""}`}
          icon="ScrollText"
          className="mt-5">
          {allTranscripts.length === 0
            ? <EmptyState icon="ScrollText" title="No meeting transcripts yet"
                description="Zoom meetings are imported automatically. Use Upload Transcript to add a meeting from Teams, Google Meet, or any other source." />
            : allTranscripts.map((tr) => {
                const { label: srcLabel, tone: srcTone } = transcriptSourceInfo(tr);
                const title    = meetingTranscriptTitle(tr, contactName);
                const time     = zoomMeetingTime(tr);
                const duration = fmtDuration((tr?.meeting_context?.zoom || {}).duration_minutes
                                   || (tr.duration_minutes));
                const linked   = hasLinkedZoomMeeting(tr);
                return (
                  <div key={tr.id} data-testid={`transcript-row-${tr.id}`}
                    className="flex items-start justify-between border-b border-slate-50 py-3 last:border-0 gap-3">
                    <div className="min-w-0 flex-1">
                      {/* Primary: source-specific transcript title */}
                      <div className="text-sm font-medium text-slate-800 truncate">{title}</div>
                      {/* Secondary: date · source · duration */}
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {time && <span className="text-xs text-slate-500">{fmtDT(time)}</span>}
                        <span className="text-slate-300 text-xs">·</span>
                        <Pill tone={srcTone} className="text-xs shrink-0">{srcLabel}</Pill>
                        {duration && <><span className="text-slate-300 text-xs">·</span><span className="text-xs text-slate-400">{duration}</span></>}
                      </div>
                      {/* Relationship: linked-to-meeting indicator — canonical check only */}
                      {linked && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-400" data-testid={`transcript-linked-${tr.id}`}>
                          <Icons.Link2 className="h-3 w-3 shrink-0" />
                          Linked to Zoom Meeting
                        </div>
                      )}
                      {/* Analysis lifecycle status intentionally omitted — belongs on the Zoom Meeting card */}
                    </div>
                    {/* Actions: View Transcript + Meeting Review.
                        Any transcript visible here is already guaranteed assigned (client_id set)
                        and non-archived by the backend /related query — the minimum canonical
                        contract Meeting Review requires. No source/provider gate is applied. */}
                    <div className="flex items-center gap-2 shrink-0">
                      <ActionButton
                        data-testid={`transcript-view-btn-${tr.id}`}
                        icon="ScrollText"
                        variant="ghost"
                        onClick={() => setViewingTranscript(tr)}
                        className="text-xs">
                        View Transcript
                      </ActionButton>
                      <ActionButton
                        data-testid={`transcript-meeting-review-btn-${tr.id}`}
                        icon="ArrowRight"
                        variant="secondary"
                        onClick={() => navigate(`/meeting-review/${tr.id}`)}
                        className="shrink-0 text-xs">
                        Meeting Review
                      </ActionButton>
                    </div>
                  </div>
                );
              })}
        </SectionCard>

        {showUploadTranscript && (
          <UploadTranscriptDialog
            open={showUploadTranscript}
            onClose={() => setShowUploadTranscript(false)}
            clientId={clientId}
            onUploaded={reload}
          />
        )}

        {/* Transcript slide-over — presentation only, no mutations */}
        {viewingTranscript && (
          <TranscriptSlideOver
            transcript={viewingTranscript}
            clientName={contactName}
            onClose={() => setViewingTranscript(null)}
          />
        )}
      </TabsContent>
      <TabsContent value="production"><ProductionWsTab clientId={clientId} /></TabsContent>

      {/* WORKFLOW */}
      <TabsContent value="workflow">
        {wf?.pipeline ? (
          <SectionCard title={wf.pipeline.name} icon="GitMerge">
            <WorkflowPipeline stages={wf.pipeline.stages} currentStage={wf.state.current_stage} history={wf.state.stage_history} percent={wf.state.percent} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <MiniCard label="Current Stage" value={intel.current_stage_label || "—"} icon="Flag" tone="blue" />
              <MiniCard label="Blocked Tasks" value={intel.overdue_tasks || 0} icon="AlertTriangle" tone="rose" />
              <MiniCard label="Pending Approvals" value={0} icon="ShieldCheck" />
              <MiniCard label="Automation" value="Idle" icon="Workflow" />
            </div>
          </SectionCard>
        ) : <EmptyState icon="GitMerge" title="No active workflow" />}
      </TabsContent>

      {/* TIMELINE */}
      <TabsContent value="timeline">
        <SectionCard title="Operational Timeline" icon="Activity">
          {timeline.length === 0 ? <EmptyState icon="Activity" title="No activity yet" /> :
            <div className="space-y-5">
              {timeline.map((e, i, arr) => (
                <TimelineItem key={e.id} icon={e.icon} title={e.title} description={e.description} meta={`${e.actor_name} · ${fmtDT(e.created_at)}`} last={i === arr.length - 1} />
              ))}
            </div>}
        </SectionCard>
      </TabsContent>

      {/* TASKS */}
      <TabsContent value="tasks">
        <SectionCard title="Tasks" icon="CheckSquare">
          <div className="flex gap-2 mb-4">
            <input value={taskText} onChange={(e) => setTaskText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} placeholder="Add a task and press Enter…" data-testid="ws-task-input"
              className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
            <ActionButton icon="Plus" onClick={addTask} data-testid="ws-task-add">Add</ActionButton>
          </div>
          {tasks.length === 0 ? <EmptyState icon="CheckSquare" title="No tasks yet" /> :
            <div className="space-y-2">
              {tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2.5" data-testid={`ws-task-${t.id}`}>
                  <div className="flex items-center gap-3">
                    {t.completed_at ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" /> :
                      <button onClick={() => completeTask(t.id)} className="text-slate-300 hover:text-emerald-500" data-testid={`ws-task-complete-${t.id}`}><Icons.Circle className="h-4 w-4" /></button>}
                    <div><div className={cn("text-sm", t.completed_at ? "line-through text-slate-400" : "text-slate-800")}>{t.title}</div>{t.due_at && <div className="text-xs text-slate-400">Due {fmtDate(t.due_at)}</div>}</div>
                  </div>
                  <Pill tone={t.priority === "high" ? "danger" : "neutral"} className="capitalize">{t.priority}</Pill>
                </div>
              ))}
            </div>}
        </SectionCard>
      </TabsContent>

      {/* NOTES */}
      <TabsContent value="notes">
        <SectionCard title="Notes" icon="StickyNote">
          <div className="flex gap-2 mb-4">
            <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Write a note and press Enter…" data-testid="ws-note-input"
              className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
            <ActionButton icon="Plus" onClick={addNote} data-testid="ws-note-add">Add</ActionButton>
          </div>
          {notes.length === 0 ? <EmptyState icon="StickyNote" title="No notes yet" /> :
            <div className="space-y-3">
              {notes.map((n) => (
                <div key={n.id} className="border border-slate-100 rounded-lg p-3" data-testid={`ws-note-${n.id}`}>
                  <div className="flex items-center justify-between mb-1"><span className="text-xs text-slate-400">{n.author_name} · {fmtDT(n.created_at)}</span>{n.pinned && <Pill tone="warning">Pinned</Pill>}</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.body}</p>
                </div>))}
            </div>}
        </SectionCard>
      </TabsContent>

      {/* PENNY INSIGHTS */}
      <TabsContent value="penny">
        <SectionCard title="Allison Insights" icon="Bot" action={<Pill tone="purple">Preview</Pill>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <MiniCard label="AI Confidence" value={`${Math.round((intel.ai_confidence || 0) * 100)}%`} icon="Gauge" tone="emerald" />
            <MiniCard label="Recommended Team" value={intel.recommended_team} icon="Users" tone="blue" />
          </div>
          <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4">
            <div className="flex items-center gap-2 mb-3"><Icons.Sparkles className="h-4 w-4 text-violet-600" /><span className="font-medium text-slate-800">Operational Intelligence</span></div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Icons.ArrowRight className="h-3.5 w-3.5 mt-1 text-violet-500" /><b>Next best action:</b>&nbsp;{intel.next_best_action?.title} — {intel.next_best_action?.description}</li>
              {tax.fbar_required && <li className="flex items-start gap-2"><Icons.ArrowRight className="h-3.5 w-3.5 mt-1 text-violet-500" />Client likely FBAR required based on foreign accounts.</li>}
              {intel.missing_documents > 0 && <li className="flex items-start gap-2"><Icons.ArrowRight className="h-3.5 w-3.5 mt-1 text-violet-500" />{intel.missing_documents} document(s) still needed for organizer completion.</li>}
              {services[0] && <li className="flex items-start gap-2"><Icons.ArrowRight className="h-3.5 w-3.5 mt-1 text-violet-500" />Suggested add-on: {services[0].name}.</li>}
              <li className="flex items-start gap-2"><Icons.ArrowRight className="h-3.5 w-3.5 mt-1 text-violet-500" />Estimated complexity: <span className="capitalize">&nbsp;{intel.estimated_complexity}</span>.</li>
            </ul>
            <p className="text-[11px] text-slate-400 mt-3 italic">Generated by Allison (heuristic preview). Full LLM-powered insights arrive with the AI Agent Framework build.</p>
          </div>
        </SectionCard>
      </TabsContent>

      {/* AUDIT HISTORY */}
      <TabsContent value="audit">
        <SectionCard title="Audit History" icon="ShieldCheck" action={<Pill tone="neutral">Read-only</Pill>}>
          <DataTable
            columns={[{ key: "created_at", label: "Timestamp" }, { key: "user_name", label: "User" }, { key: "action", label: "Action" }, { key: "module", label: "Module" }, { key: "summary", label: "Detail" }]}
            rows={audit || []}
            renderCell={(r, col) => col.key === "created_at" ? <span className="text-slate-500 text-xs">{fmtDT(r.created_at)}</span>
              : col.key === "action" ? <Pill tone="neutral">{r.action}</Pill>
              : col.key === "module" ? <span className="capitalize text-slate-500">{r.module || "—"}</span>
              : <span className="text-slate-700">{r[col.key] || "—"}</span>}
            empty={<EmptyState icon="ShieldCheck" title="No audit records" />} />
        </SectionCard>
      </TabsContent>
    </Tabs>
  );
}

// ── Invoices Workspace Tab ──────────────────────────────────────────────────
// Currency-aware formatter for the workspace invoice list.
// Falls back gracefully if the currency code is unrecognised.
function fmtMoney(v, ccy) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: ccy || "USD", maximumFractionDigits: 2,
    }).format(Number(v || 0));
  } catch {
    return `${ccy || "USD"} ${Number(v || 0).toFixed(2)}`;
  }
}

// Thin staff-facing invoice list for a single client.
// Uses existing GET /api/finance/invoices?client_id={id} endpoint.
// Clicking an invoice navigates to the canonical /finance/invoices/:id detail route.
// No duplicate finance logic. No new collections.
const INVOICE_STATUS_MAP = {
  draft:            { label: "Draft",          tone: "slate"   },
  awaiting_payment: { label: "Awaiting Payment", tone: "amber"  },
  partial:          { label: "Partially Paid", tone: "amber"   },
  paid:             { label: "Paid",           tone: "emerald" },
  void:             { label: "Void",           tone: "red"     },
  cancelled:        { label: "Cancelled",      tone: "red"     },
  historical:       { label: "Historical",     tone: "slate"   },
};

function InvoicesWorkspaceTab({ clientId, canViewFinance, lifecycleInvoices }) {
  const navigate = useNavigate();
  const [invoices, setInvoices]   = useState(null);
  const [search,   setSearch]     = useState("");
  const [filter,   setFilter]     = useState("all");

  useEffect(() => {
    if (canViewFinance) {
      // Financial users: fetch full invoice data from the Finance module.
      api.get(`/finance/invoices?client_id=${clientId}&limit=200`)
        .then(r => setInvoices(r.data?.items || r.data || []))
        .catch(() => setInvoices([]));
    } else {
      // Non-financial users: use lifecycle-only invoice data from the already-sanitized
      // CRM /related payload. No financial amounts, totals, or balance fields are present.
      setInvoices(lifecycleInvoices || []);
    }
  }, [clientId, canViewFinance, lifecycleInvoices]);

  const allInvoices = invoices || [];

  const open = allInvoices.filter(i => ["awaiting_payment", "partial"].includes(i.status));
  const paid = allInvoices.filter(i => i.status === "paid");
  const voided = allInvoices.filter(i => ["void", "cancelled"].includes(i.status));
  // Determine the dominant currency from outstanding invoices for the KPI card.
  // If client has mixed-currency invoices this uses the first open invoice's currency;
  // same-currency clients (common) get exact totals.
  const outstandingCurrency = open.find(i => i.currency)?.currency
    || allInvoices.find(i => i.currency)?.currency
    || "USD";
  const totalOutstanding = open
    .filter(i => (i.currency || "USD") === outstandingCurrency)
    .reduce((s, i) => s + (i.balance || 0), 0);
  // Defense-in-depth: only show financial MiniCard when the caller is financial.
  const hasFinancialData = canViewFinance && allInvoices.some(i => i.total != null || i.balance != null);

  const filtered = allInvoices.filter(i => {
    const q = search.toLowerCase();
    const matchSearch = !q || (i.invoice_number || "").toLowerCase().includes(q) ||
      (i.line_items || []).some(li => (li.name || li.description || "").toLowerCase().includes(q));
    const matchFilter = filter === "all" ||
      (filter === "open"  && ["awaiting_payment", "partial"].includes(i.status)) ||
      (filter === "paid"  && i.status === "paid") ||
      (filter === "void"  && ["void", "cancelled"].includes(i.status)) ||
      (filter === "other" && !["awaiting_payment", "partial", "paid", "void", "cancelled"].includes(i.status));
    return matchSearch && matchFilter;
  });

  if (!invoices) return (
    <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
      <Icons.Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Loading invoices…</span>
    </div>
  );

  return (
    <div data-testid="ws-invoices-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <MiniCard label="Open" value={open.length} tone="amber" icon="Clock" />
        <MiniCard label="Paid" value={paid.length} tone="emerald" icon="CheckCircle" />
        <MiniCard label="Void" value={voided.length} tone="slate" icon="XCircle" />
        {hasFinancialData && <MiniCard label="Outstanding" value={fmtMoney(totalOutstanding, outstandingCurrency)} tone={totalOutstanding > 0 ? "amber" : "slate"} icon="DollarSign" />}
      </div>

      {/* Search + filter */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices…"
            className="w-full pl-9 pr-3 h-9 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            data-testid="ws-invoices-search" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="h-9 rounded-lg border border-slate-200 bg-white text-sm px-3 focus:outline-none"
          data-testid="ws-invoices-filter">
          <option value="all">All statuses</option>
          <option value="open">Awaiting Payment</option>
          <option value="paid">Paid</option>
          <option value="void">Void / Cancelled</option>
          <option value="other">Draft / Historical</option>
        </select>
      </div>

      {/* Invoice list */}
      {filtered.length === 0 ? (
        <EmptyState icon="Receipt" title="No invoices" description={search || filter !== "all" ? "No invoices match your filter." : "No invoices on record for this client."} />
      ) : (
        <div className="space-y-2" data-testid="ws-invoices-list">
          {filtered.map(inv => {
            const statusInfo = INVOICE_STATUS_MAP[inv.status] || { label: inv.status, tone: "slate" };
            const primaryLine = (inv.line_items && inv.line_items.length > 0)
              ? (inv.line_items[0].name || inv.line_items[0].description || "Invoice")
              : (inv.description || "Invoice");
            const extraLines = inv.line_items && inv.line_items.length > 1
              ? `+ ${inv.line_items.length - 1} more line item${inv.line_items.length > 2 ? "s" : ""}`
              : null;
            return (
              <button key={inv.id} onClick={() => navigate(`/finance/invoices/${inv.id}`)}
                className="w-full text-left rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all p-4 group"
                data-testid={`ws-invoice-row-${inv.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-mono text-xs text-slate-400">{inv.invoice_number}</span>
                      <StatusBadge status={inv.status} map={Object.fromEntries(Object.entries(INVOICE_STATUS_MAP).map(([k, v]) => [k, v.tone]))} />
                    </div>
                    <div className="text-sm font-medium text-slate-800 truncate">{primaryLine}</div>
                    {extraLines && <div className="text-xs text-slate-400">{extraLines}</div>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {inv.issue_date && <span>Issued {inv.issue_date.slice(0, 10)}</span>}
                      {canViewFinance && inv.balance > 0 && <span className="text-amber-600 font-medium">Balance {fmtMoney(inv.balance, inv.currency)}</span>}
                      {canViewFinance && inv.balance <= 0 && inv.status === "paid" && <span className="text-emerald-600 font-medium">Paid in full</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {canViewFinance && inv.total != null && <div className="text-sm font-bold text-slate-900">{fmtMoney(inv.total, inv.currency)}</div>}
                    <Icons.ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 ml-auto mt-1" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
